import {IResourceComponentsProps, useShow, useTranslate, useDelete, useNotification} from "@refinedev/core";
import { useNavigate } from "react-router-dom";
import {BooleanField, DateField, EmailField, List, NumberField, EditButton, DeleteButton} from "@refinedev/mui";
import {Grid, Paper, Stack, Typography, Alert, Box, Button, Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions} from "@mui/material";
import Avatar from "@mui/material/Avatar";
import React, { useEffect, useState } from "react";
import {DataGrid, GridColDef} from "@mui/x-data-grid";
import {getDuration} from "../../utils/getDuration";
import {AddressCell} from "../../components/table/AdressCell";
import { useAuth } from "../../contexts/auth-context";
import { canAccessUserData } from "../../utils/role-permissions";
import { Trash2 } from "lucide-react";
import axiosInstance from "../../api/axios-config";


export const UserShow: React.FC<IResourceComponentsProps> = () => {
    const translate = useTranslate();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const {queryResult} = useShow();
    const {data, isLoading, error} = queryResult;
    const { mutate: deleteUser } = useDelete();
    const { open } = useNotification();
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

        const record = data?.data;

    // Define columns first to avoid conditional hook usage
    const columns = React.useMemo<GridColDef[]>(
        () => [
            {
                field: "createdAt",
                flex: 1,
                headerName: translate("alert-history.fields.createdAt"),
                minWidth: 250,
                renderCell: function render({value}) {
                    return <DateField value={value}/>;
                },
            },
            {
                field: "Adresse",
                flex: 1,
                headerName: translate("alert-history.fields.address"),
                type: "string",
                minWidth: 250,
                renderCell: function render(value) {
                    return <AddressCell latitude={value.row.latitude} longitude={value.row.longitude} />;
                }
            },
            {
                field: "duration",
                flex: 1,
                headerName: translate("alert-history.fields.duration"),
                minWidth: 100,
                renderCell: function render({row}) {
                    return <p>{getDuration({
                        createdAt: row?.createdAt,
                        endedAt: row?.endedAt,
                    })}</p>;
                }
            }
        ],
        [translate],
    );

        // Company-based access control
    useEffect(() => {
        if (!isLoading && record && currentUser) {
            // Special handling for VIGILANE role
            if (currentUser.role === 'VIGILANE') {
                // VIGILANE can only access users with vigilaneId
                if (!record.vigilaneId) {
                    console.warn(`🚫 Access denied: VIGILANE user ${currentUser.id} tried to access user ${record.id} without vigilaneId`);
                    navigate('/users', {
                        replace: true
                    });
                    return;
                }
            } else {
                // For other roles: check company-based access
                if (!canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
                    console.warn(`🚫 Access denied: User ${currentUser.id} tried to access user ${record.id} from different company`);
                    navigate('/users', {
                        replace: true
                    });
                    return;
                }
            }
        }
    }, [record, currentUser, isLoading, navigate]);

        // Don't render if access should be denied
    if (!isLoading && record && currentUser) {
        if (currentUser.role === 'VIGILANE') {
            // VIGILANE can only access users with vigilaneId
            if (!record.vigilaneId) {
                return null; // Component will unmount due to navigation
            }
        } else {
            // For other roles: check company-based access
            if (!canAccessUserData(currentUser, record as { id: number; entrepriseId?: number })) {
                return null; // Component will unmount due to navigation
            }
        }
    }

    // Show loading or error states
    if (isLoading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '200px' }}>
                <Typography>Loading...</Typography>
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error">
                {translate("errors.loadUser", "Failed to load user information")}
            </Alert>
        );
    }

    const handleDeleteClick = () => {
        setDeleteDialogOpen(true);
    };

    const handleDeleteConfirm = async () => {
        if (record?.id) {
            // For VIGILANE role: only unlink from Vigilane, don't delete user
            if (isVigilane) {
                try {
                    await axiosInstance.patch(`/users/${record.id}`, {
                        vigilaneId: null,
                        hasVigilane: false,
                    });

                    open?.({
                        type: "success",
                        message: translate("user.unlink.success"),
                    });

                    setDeleteDialogOpen(false);
                    navigate('/users');
                } catch (error: any) {
                    open?.({
                        type: "error",
                        message: translate("user.delete.error"),
                        description: error?.response?.data?.message || "Une erreur est survenue",
                    });
                }
            } else {
                // For other roles: delete user completely
                deleteUser(
                    {
                        resource: "users",
                        id: record.id,
                    },
                    {
                        onSuccess: () => {
                            setDeleteDialogOpen(false);
                            navigate('/users');
                        },
                    }
                );
            }
        }
    };

    const handleDeleteCancel = () => {
        setDeleteDialogOpen(false);
    };

    // Check if current user has permission to delete
    const canDelete = currentUser?.role && ["ADMIN", "MODERATOR", "MODERATOR_FREE", "GUEST", "VIGILANE"].includes(currentUser.role);
    const canEdit = currentUser?.role && ["ADMIN", "MODERATOR", "MODERATOR_FREE"].includes(currentUser.role);
    const isVigilane = currentUser?.role === "VIGILANE";

    return (
        <>
        <Grid container spacing={2}>
            <Grid item xs={12} lg={3}>
                <Paper sx={{p: 2, paddingX: {xs: 4, md: 2}}}>
                    <Stack alignItems="center" spacing={1}>
                        <Avatar
                            src={record?.profilePicture}
                            sx={{width: 120, height: 120}}
                        />
                        <Typography variant="h6">
                            {record?.fullname}
                        </Typography>
                        <Stack direction="row" spacing={1}>
                            {canEdit && (
                                <EditButton
                                    resource="users"
                                    recordItemId={record?.id}
                                    size="small"
                                />
                            )}
                            {canDelete && (
                                <Button
                                    variant="outlined"
                                    color="error"
                                    size="small"
                                    startIcon={<Trash2 size={16} />}
                                    onClick={handleDeleteClick}
                                >
                                    {translate(isVigilane ? "user.unlink.button" : "user.delete.button")}
                                </Button>
                            )}
                        </Stack>
                    </Stack>
                    <br/>
                    <Stack spacing={1}>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.phone")}
                        </Typography>
                        <Typography variant="body1" sx={{ fontFamily: 'monospace', fontSize: '0.95rem' }}>
                            {record?.phone ?? translate("user.fields.phone.notProvided", "Not provided")}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.email")}
                        </Typography>
                        <EmailField value={record?.email}/>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.role")}
                        </Typography>
                        <Typography variant="body1" sx={{
                            textTransform: 'capitalize',
                            color: record?.role === 'ADMIN' ? 'error.main' :
                                   record?.role === 'MODERATOR' ? 'warning.main' :
                                   'text.primary'
                        }}>
                            {record?.role ?? translate("user.fields.role.notDefined", "Not defined")}
                        </Typography>
                        <Typography variant="body1" fontWeight="bold">
                            {translate("user.fields.company")}
                        </Typography>
                        <Typography variant="body1">
                            {record?.entreprise?.nom ?? translate("user.fields.company.notAssigned", "Not assigned to any company")}
                        </Typography>
                    </Stack>
                </Paper>
            </Grid>
            <Grid item xs={12} lg={9}>
                <List
                    headerProps={{
                        title: translate("alert-history.title"),
                    }}
                    canCreate={false}
                >
                    {record?.emergencyHistoryProprietary && <DataGrid
                        columns={columns}
                        rows={record?.emergencyHistoryProprietary}
                        autoHeight
                        initialState={{
                            pagination: {
                                paginationModel: {page: 0, pageSize: 5},
                            },
                        }}
                        pageSizeOptions={[5, 10, 20, 100]}
                        pagination
                    />}
                </List>
            </Grid>
        </Grid>

        {/* Delete Confirmation Dialog */}
        <Dialog
            open={deleteDialogOpen}
            onClose={handleDeleteCancel}
            aria-labelledby="delete-dialog-title"
            aria-describedby="delete-dialog-description"
        >
            <DialogTitle id="delete-dialog-title">
                {translate(isVigilane ? "user.unlink.confirm.title" : "user.delete.confirm.title")}
            </DialogTitle>
            <DialogContent>
                <DialogContentText id="delete-dialog-description">
                    {translate(isVigilane ? "user.unlink.confirm.message" : "user.delete.confirm.message")}
                </DialogContentText>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleDeleteCancel} color="primary">
                    {translate("user.delete.confirm.cancel")}
                </Button>
                <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                    {translate(isVigilane ? "user.unlink.confirm.confirm" : "user.delete.confirm.confirm")}
                </Button>
            </DialogActions>
        </Dialog>
        </>
    );
};
